import { htmlUnescape } from 'escape-goat';
import {
  actionTypes,
  BaseShellAction,
  BuildAndDeployAction,
  FileAction,
  GithubSyncAction,
  ImportAction,
  ShellAction,
  StartAction,
  SupabaseAction,
  type ActionType,
  type BoltAction,
  type FileActionContentType,
  type SupabaseActionType,
} from './actions';
import type { BoltArtifactData } from '~/types/artifact';
import { camelToDashCase } from '~/utils/cases';
import { invariant } from '~/utils/invariant';
import { createScopedLogger } from '~/utils/logger';
import { unreachable } from '~/utils/unreachable';
import { addWorkDir } from '~/utils/workDir';

const KILL_TAG_OPEN = '<kill>';
const KILL_TAG_CLOSE = '</kill>';
const ARTIFACT_TAG_OPEN = '<boltArtifact';
const ARTIFACT_TAG_CLOSE = '</boltArtifact>';
const ARTIFACT_ACTION_TAG_OPEN = '<boltAction';
const ARTIFACT_ACTION_TAG_CLOSE = '</boltAction>';

const logger = createScopedLogger('MessageParser');

export interface ArtifactCallbackData extends BoltArtifactData {
  messageId: string;
}

export interface ActionCallbackData {
  artifactId: string;
  messageId: string;
  action: BoltAction;
}

export interface ActionFixCallbackData {
  type?: string;
  newType?: string;
}

export interface PartialFileContent {
  path: string;
  content: string;
  status: 'start' | 'stream' | 'end';
}

interface StreamFileCallbackData {
  action: FileAction;
  data: PartialFileContent;
}

export type ArtifactCallback = (data: ArtifactCallbackData, shouldRun: boolean) => void;
export type ActionCallback = (data: ActionCallbackData, shouldRun: boolean) => void;
export type ActionFixCallback = (data: ActionFixCallbackData) => void;
export type PartialFileCallback = (data: StreamFileCallbackData) => string | undefined | void;

export interface ParserCallbacks {
  onArtifactOpen?: ArtifactCallback;
  onArtifactClose?: ArtifactCallback;
  onActionOpen?: ActionCallback;
  onActionClose?: ActionCallback;
  onActionFix?: ActionFixCallback;
  onFileStream?: PartialFileCallback;
}

interface ElementFactoryProps {
  artifactId: string;
  runningActions: string;
  artifactComponent?: string;
}

type ElementFactory = (props: ElementFactoryProps) => string;

export interface StreamingMessageParserOptions {
  callbacks?: ParserCallbacks;
  artifactElement?: ElementFactory;
  artifactComponent?: ElementFactory;
}

interface MessageState {
  position: number;
  insideArtifact: boolean;
  insideAction: boolean;
  currentArtifact?: BoltArtifactData;
  currentAction?: BoltAction;
  streamingFile: boolean;
  output: string;
}

export class StreamingMessageParser {
  #artifactId = 1;
  #actionId = 1;
  #messages = new Map<string, MessageState>();

  constructor(private _options: StreamingMessageParserOptions = {}) {}

  parse(messageId: string, input: string, runActions = false) {
    let state = this.#messages.get(messageId);

    if (!state) {
      state = {
        position: 0,
        insideAction: false,
        insideArtifact: false,
        streamingFile: false,
        output: '',
      };

      this.#messages.set(messageId, state);
    }

    let output = '';
    let i = state.position;
    let earlyBreak = false;

    while (i < input.length) {
      if (state.insideArtifact) {
        const currentArtifact = state.currentArtifact;

        if (currentArtifact === undefined) {
          unreachable('Artifact not initialized');
        }

        if (state.insideAction) {
          const closeIndex = input.indexOf(ARTIFACT_ACTION_TAG_CLOSE, i);
          const actionOpenIndex = input.indexOf(ARTIFACT_ACTION_TAG_OPEN, i);
          const artifactCloseIndex = input.indexOf(ARTIFACT_TAG_CLOSE, i);

          const currentAction = state.currentAction;

          if (closeIndex !== -1 || artifactCloseIndex !== -1 || actionOpenIndex !== -1) {
            // we end either at the first element
            const endIndex = smallestNonNegativeValue(closeIndex, artifactCloseIndex, actionOpenIndex);

            // if currentAction is undefined it means that this is an unknown action that was received
            if (currentAction) {
              const content = (currentAction.content.value + input.slice(i, endIndex)).trim();

              currentAction.content.set(content);

              if (currentAction instanceof BaseShellAction) {
                this.#parseShellTag(content, currentAction);
              } else if (currentAction.type === 'build-and-deploy') {
                this.#parseBuildTag(content, currentAction);
              }

              if (currentAction.type === 'file') {
                const path = addWorkDir(currentAction.filePath);

                state.streamingFile = false;

                const updatedContent = this._options.callbacks?.onFileStream?.({
                  action: currentAction,
                  data: { path, content, status: 'end' },
                });

                if (updatedContent !== undefined) {
                  currentAction.content.set(updatedContent);
                }
              }

              this._options.callbacks?.onActionClose?.(
                {
                  artifactId: currentArtifact.id,
                  messageId,
                  action: currentAction,
                },
                runActions,
              );
            }

            state.insideAction = false;
            state.currentAction = undefined;

            if (actionOpenIndex === endIndex) {
              // we need to parse the new action
              i = actionOpenIndex;
            } else if (artifactCloseIndex === endIndex) {
              this._options.callbacks?.onArtifactClose?.({ messageId, ...currentArtifact }, runActions);

              state.insideArtifact = false;
              state.currentArtifact = undefined;

              i = artifactCloseIndex + ARTIFACT_TAG_CLOSE.length;
            } else {
              i = closeIndex + ARTIFACT_ACTION_TAG_CLOSE.length;
            }
          } else {
            if (currentAction?.type === 'file') {
              const isStreamStart = !state.streamingFile;

              if (isStreamStart) {
                state.streamingFile = true;
              }

              const path = addWorkDir(currentAction.filePath);

              this._options.callbacks?.onFileStream?.({
                action: currentAction,
                data: {
                  path,
                  content: input.slice(i, -ARTIFACT_ACTION_TAG_CLOSE.length).trim(),
                  status: isStreamStart ? 'start' : 'stream',
                },
              });
            }

            break;
          }
        } else {
          const actionOpenIndex = input.indexOf(ARTIFACT_ACTION_TAG_OPEN, i);
          const artifactCloseIndex = input.indexOf(ARTIFACT_TAG_CLOSE, i);

          if (actionOpenIndex !== -1 && (artifactCloseIndex === -1 || actionOpenIndex < artifactCloseIndex)) {
            const actionEndIndex = input.indexOf('>', actionOpenIndex);

            if (actionEndIndex !== -1) {
              state.insideAction = true;

              state.currentAction = this.#parseActionTag(
                currentArtifact.id,
                messageId,
                input,
                actionOpenIndex,
                actionEndIndex,
                runActions,
              );

              if (state.currentAction) {
                this._options.callbacks?.onActionOpen?.(
                  {
                    artifactId: currentArtifact.id,
                    messageId,
                    action: state.currentAction as BoltAction,
                  },
                  runActions,
                );
              }

              i = actionEndIndex + 1;
            } else {
              break;
            }
          } else if (artifactCloseIndex !== -1) {
            this._options.callbacks?.onArtifactClose?.({ messageId, ...currentArtifact }, runActions);

            state.insideArtifact = false;
            state.currentArtifact = undefined;

            i = artifactCloseIndex + ARTIFACT_TAG_CLOSE.length;
          } else {
            break;
          }
        }
      } else if (input[i] === '<' && input[i + 1] !== '/') {
        let j = i;
        let potentialTag = '';

        while (j < input.length && potentialTag.length < ARTIFACT_TAG_OPEN.length) {
          potentialTag += input[j];

          if (potentialTag === ARTIFACT_TAG_OPEN) {
            const nextChar = input[j + 1];

            if (nextChar && nextChar !== '>' && nextChar !== ' ') {
              output += input.slice(i, j + 1);
              i = j + 1;
              break;
            }

            const openTagEnd = input.indexOf('>', j);

            if (openTagEnd !== -1) {
              const artifactTag = input.slice(i, openTagEnd + 1);

              const artifactTitle = this.#extractAttribute(artifactTag, 'title') as string;
              const artifactComponent = this.#extractAttribute(artifactTag, 'component') as string;
              const artifactId = this.#extractAttribute(artifactTag, 'id') as string;

              if (!artifactTitle) {
                logger.warn('Artifact title missing');
              }

              if (!artifactId) {
                logger.warn('Artifact id missing');
              }

              state.insideArtifact = true;

              const currentArtifact = {
                id: `${this.#artifactId++}:${artifactId}`,
                slug: artifactId,
                title: artifactTitle,
                ...(artifactComponent ? { component: artifactComponent } : {}),
              } satisfies BoltArtifactData;

              state.currentArtifact = currentArtifact;

              this._options.callbacks?.onArtifactOpen?.({ messageId, ...currentArtifact }, runActions);

              const artifactFactory = this._options.artifactElement ?? createArtifactElement;

              output += artifactFactory({
                artifactId: currentArtifact.id,
                artifactComponent: currentArtifact.component,
                runningActions: runActions.toString(),
              });

              i = openTagEnd + 1;
            } else {
              earlyBreak = true;
            }

            break;
          } else if (!ARTIFACT_TAG_OPEN.startsWith(potentialTag)) {
            output += input.slice(i, j + 1);
            i = j + 1;
            break;
          }

          j++;
        }

        if (j === input.length && ARTIFACT_TAG_OPEN.startsWith(potentialTag)) {
          break;
        }
      } else {
        output += input[i];
        i++;
      }

      if (earlyBreak) {
        break;
      }
    }

    state.position = i;

    state.output += output;

    return state.output;
  }

  reset() {
    this.#actionId = 1;
    this.#artifactId = 1;
    this.#messages.clear();
  }

  #getNextActionId() {
    return String(this.#actionId++);
  }

  #parseActionTag(
    artifactId: string,
    messageId: string,
    input: string,
    actionOpenIndex: number,
    actionEndIndex: number,
    runAction: boolean,
  ) {
    const actionTag = input.slice(actionOpenIndex, actionEndIndex + 1);

    const { actionType, ...fixed } = this.#fixAction(this.#extractAttribute(actionTag, 'type'));

    const id = this.#getNextActionId();

    if (actionType === 'file') {
      const filePath = this.#extractAttribute(actionTag, 'filePath') || fixed.filePath;
      const contentType = this.#extractAttribute(actionTag, 'contentType') as FileActionContentType | undefined;

      if (!filePath) {
        logger.warn('File path not specified');

        return undefined;
      }

      return new FileAction(artifactId, messageId, id, filePath, runAction, contentType);
    }

    if (actionType.startsWith('supabase-')) {
      return new SupabaseAction(artifactId, messageId, id, actionType.slice(9) as SupabaseActionType);
    }

    if (actionType === 'start') {
      return new StartAction(artifactId, messageId, id, false);
    }

    if (actionType === 'shell') {
      return new ShellAction(artifactId, messageId, id);
    }

    if (actionType === 'import') {
      const target = this.#extractAttribute(actionTag, 'target') as string;
      const kind = this.#extractAttribute(actionTag, 'kind');
      const cloning = this.#extractAttribute(actionTag, 'cloning');

      return new ImportAction(artifactId, messageId, id, target, kind, cloning);
    }

    if (actionType === 'deploy') {
      const provider = this.#extractAttribute(actionTag, 'provider') as string;

      return new BuildAndDeployAction(artifactId, messageId, id, provider);
    }

    if (actionType === 'github-sync') {
      const oid = this.#extractAttribute(actionTag, 'oid') as string;

      return new GithubSyncAction(artifactId, messageId, id, oid);
    }

    logger.warn(`Unknown action type '${actionType}'`);

    return undefined;
  }

  #parseShellTag(input: string, shellAction: BaseShellAction) {
    const runCommand = this.#extractTag(input, 'command');

    const commandsToKill: string[] = [];

    let killIndex = 0;

    while ((killIndex = input.indexOf(KILL_TAG_OPEN, killIndex)) !== -1) {
      const killEndIndex = input.indexOf(KILL_TAG_CLOSE, killIndex);

      if (killEndIndex !== -1) {
        const commandToKill = input.slice(killIndex + KILL_TAG_OPEN.length, killEndIndex).trim();

        if (commandToKill) {
          commandsToKill.push(commandToKill);
        }

        killIndex = killEndIndex + KILL_TAG_CLOSE.length;
      } else {
        break;
      }
    }

    shellAction.content.set(runCommand ?? '');
  }

  #parseBuildTag(input: string, action: BuildAndDeployAction) {
    const build = this.#extractTag(input, 'build');

    if (build) {
      const command = this.#extractTag(build, 'command');
      const output = this.#extractTag(build, 'output');

      if (command && output) {
        action.setBuildConfig({ command, output });
      }
    }
  }

  #extractTag(input: string, tagName: string): string | undefined {
    const match = input.match(new RegExp(`<${tagName}>([\\s\\S]*?)</${tagName}>`));
    return match ? match[1].trim() : undefined;
  }

  #extractAttribute(tag: string, attributeName: string): string | undefined {
    const match = tag.match(new RegExp(`${attributeName}="([^"]*)"`, 'i'));
    return match ? htmlUnescape(match[1]) : undefined;
  }

  #fixAction(actionType: string | undefined): { actionType: ActionType | (string & {}); filePath?: string } {
    if (actionType && (actionTypes.has(actionType as ActionType) || actionType.startsWith('supabase-'))) {
      return { actionType };
    }

    // filename used as action type, e.g. <boltAction type="package.json">
    if (actionType?.includes('.') && !actionType.endsWith('.')) {
      this._options.callbacks?.onActionFix?.({ type: actionType, newType: 'file' });

      return { actionType: 'file', filePath: actionType };
    }

    this._options.callbacks?.onActionFix?.({ type: actionType });

    return { actionType: actionType || '' };
  }
}

const createArtifactElement: ElementFactory = (props) => {
  const elementProps = [
    'class="__boltArtifact__"',
    ...Object.entries(props)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => {
        return `data-${camelToDashCase(key)}=${JSON.stringify(value)}`;
      }),
  ];

  return `<div ${elementProps.join(' ')}></div>`;
};

function smallestNonNegativeValue(...values: number[]): number {
  let smallest = Number.MAX_SAFE_INTEGER;

  for (const value of values) {
    if (value >= 0) {
      smallest = Math.min(value, smallest);
    }
  }

  invariant(smallest !== Number.MAX_SAFE_INTEGER, 'No positive values were provided');

  return smallest;
}
